import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Fade,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  Switch,
  SxProps,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  TextFieldProps,
  Tooltip,
  Typography,
} from '@mui/material';

import RefreshIcon from '@mui/icons-material/Refresh';

import { useAppSelector } from '../../../../../../hooks';
import { checkUserPermission } from '../../../../../../helper';

import { USER_PERMISSION_INDIVIDUALS } from '../../../../../../store/V2/RolesSlice';
import { getAuthState } from '../../../../../../store/AuthSlice';
import useTable, {
  defaultRowsPerPageOptions,
  TUseTableState,
} from '../../../../../../components/V2/Table/type1/useTable';
import DarkLoadingButton, {
  DarkButton,
  defaultButtonStyles,
} from '../../../../../../components/V2/Buttons';
import TableHeadCustom, {
  THeadLabelItem,
} from '../../../../../../components/V2/Table/type1/TableHeadCustom';
import { TPTextField } from '../../../../../../components/Inputs';
import SitemapTableRow from './SitemapTableRow';
import TableNoData from '../../../../../../components/V2/Empty/TableNoData';
import TableLoader from '../../../../../../components/Loaders/TableLoader';
import TablePaginationActions from '../../../../../../components/V2/Table/type1/TablePaginationActions';

import {
  adminGenerateSitemap,
  adminGetSitemaps,
  getSitemapState,
  resetAdminSitemapListData,
  TAdminGetSitemaps,
} from '../../../../../../store/V2/SitemapSlice';
import { getTenantsState } from '../../../../../../store/V2/TenantsSlice';

const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const tableCardContainerStyles: SxProps = {
  borderRadius: '20px',
  boxShadow: '0px 14px 40px 0px #6E9DB22E',
};

type TState = {
  searchText: string;
};

type TFilterData = {
  limit: number;
  offset: number;
  sortKey: string;
  sortValue: number;
  searchText?: string;
};

const defaultListLimit = 30;
const defaultOrderBy = 'createdAt';
const defaultOrder = 'desc';
const defaultTableData: TUseTableState = {
  defaultOrderBy,
  defaultOrder,
  defaultDense: false,
  defaultCurrentPage: 0,
  defaultRowsPerPage: defaultRowsPerPageOptions[0],
  defaultSelected: [],
};

const initialState: TState = {
  searchText: '',
};

const defaultFilterData: TFilterData = {
  offset: 0,
  limit: defaultListLimit,
  sortKey: defaultOrderBy,
  sortValue: defaultOrder === 'desc' ? -1 : 1,
};

const tableHeaderLabels: THeadLabelItem[] = [
  {
    id: 'name',
    label: 'Name',
    sortable: true,
  },
  {
    id: 'status',
    label: 'Status',
    sortable: false,
  },
  {
    id: 'urlCount',
    label: 'Url Count',
    sortable: false,
  },
  {
    id: 'createdAt',
    label: 'Created At',
    sortable: true,
  },
];

const SitemapTab: FunctionComponent = () => {
  const dispatch = useDispatch();

  // Store
  const authState = useAppSelector(getAuthState);
  const tenantState = useAppSelector(getTenantsState);
  const siteMapState = useAppSelector(getSitemapState);

  const { user: authUser } = authState;
  const { appTenant } = tenantState;
  const { adminSitemapListData, status } = siteMapState;

  // State
  const tableData = useTable(defaultTableData);
  const [state, setState] = useState<TState>({ ...initialState });
  const [isLoading, setIsLoading] = useState(false);
  const [filterData, setFilterData] = useState({ ...defaultFilterData });

  const sitemapsList = adminSitemapListData?.list ?? [];
  const totalCount = adminSitemapListData?.totalDocs || 0;

  const createPermission = checkUserPermission({
    authUser,
    tenant: appTenant,
    permissions: {
      adminSettings: [
        USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_SITEMAP_CREATE,
      ],
    },
  });

  useEffect(() => {
    getSitemapsListData({ ...defaultFilterData });

    return () => {
      dispatch(resetAdminSitemapListData());
    };
  }, []);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const getSitemapsListData = async (filterDataAttributes: TFilterData) => {
    const apiData: TAdminGetSitemaps = {
      limit: filterDataAttributes.limit,
      offset: filterDataAttributes.offset,
    };

    if (filterDataAttributes.sortKey) {
      apiData.sortKey = filterDataAttributes.sortKey;
    }

    if (filterDataAttributes.sortValue) {
      apiData.sortValue = filterDataAttributes.sortValue;
    }

    if (filterDataAttributes.searchText) {
      apiData.searchText = filterDataAttributes.searchText;
    }

    dispatch(adminGetSitemaps({ ...apiData }));

    setFilterData({ ...filterDataAttributes });
  };

  const handleSort = (id: string) => {
    let newSortValue = 1;

    if (filterData.sortKey === id && filterData.sortValue === 1) {
      newSortValue = -1;
    }

    getSitemapsListData({
      ...filterData,
      sortKey: id,
      sortValue: newSortValue,
    });

    tableData.onSort(id);
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number
  ) => {
    getSitemapsListData({
      ...filterData,
      offset: page * filterData.limit,
    });

    tableData.onChangePage(event, page);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    getSitemapsListData({
      ...filterData,
      offset: 0,
      limit: parseInt(event.target.value, 10),
    });

    tableData.onChangeRowsPerPage(event);
  };

  const handleGenerate = async () => {
    setIsLoading(true);
    await dispatch(adminGenerateSitemap());
    setIsLoading(false);
    getSitemapsListData({ ...defaultFilterData });
  };

  const handleSearch = () => {
    getSitemapsListData({
      ...filterData,
      searchText: state.searchText.trim().length ? state.searchText : undefined,
    });
  };

  return (
    <Fade in>
      <div>
        <Card sx={tableCardContainerStyles}>
          <CardContent style={{ padding: '20px 0' }}>
            <div style={{ padding: '20px' }}>
              <div>
                <Typography fontSize="24px" fontWeight={600}>
                  Sitemap
                </Typography>
              </div>
              {createPermission ? (
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="flex-end"
                >
                  <DarkLoadingButton
                    loading={isLoading}
                    onClick={handleGenerate}
                    sx={{ ...defaultButtonStyles }}
                  >
                    Generate Sitemap
                  </DarkLoadingButton>
                </Stack>
              ) : null}
            </div>
            <div style={{ marginBottom: '20px', padding: '0 20px' }}>
              <Grid
                container
                columnSpacing="16px"
                rowSpacing="20px"
                alignItems="center"
              >
                <Grid item xs={12} sm={12} md={6} lg={4}>
                  <Stack direction="row" alignItems="center" gap="20px">
                    <TPTextField
                      fullWidth
                      {...textFieldProps}
                      label="Search"
                      placeholder="Enter here..."
                      value={state.searchText}
                      onChange={(event) =>
                        updateStateAttributes({
                          searchText: event.target.value,
                        })
                      }
                      onKeyDown={(e) => {
                        if (
                          e.key === 'Enter' &&
                          !(
                            !state.searchText.trim().length &&
                            !filterData.searchText?.trim().length
                          )
                        ) {
                          handleSearch();
                        }
                      }}
                    />
                    <DarkButton
                      variant="contained"
                      color="inherit"
                      sx={{ ...defaultButtonStyles }}
                      onClick={handleSearch}
                      disabled={
                        !state.searchText.trim().length &&
                        !filterData.searchText?.trim().length
                      }
                    >
                      Search
                    </DarkButton>

                    <Tooltip title="Refresh List">
                      <IconButton
                        onClick={() =>
                          getSitemapsListData({ ...defaultFilterData })
                        }
                      >
                        <RefreshIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Grid>
              </Grid>
            </div>
            <TableContainer
              sx={{ position: 'relative', maxHeight: 'calc(100vh - 250px)' }}
            >
              <Table stickyHeader size={tableData.dense ? 'small' : 'medium'}>
                <TableHeadCustom
                  order={tableData.order}
                  orderBy={tableData.orderBy}
                  headLabels={tableHeaderLabels}
                  rowCount={0}
                  numSelected={tableData.selected.length}
                  onSort={handleSort}
                />
                <TableBody>
                  {sitemapsList.map((row) => (
                    <SitemapTableRow key={row._id} sitemap={row} />
                  ))}

                  {status !== 'loading' && !sitemapsList.length ? (
                    <TableNoData title="No sitemaps are available" />
                  ) : null}

                  {status === 'loading' && !sitemapsList.length ? (
                    <TableLoader />
                  ) : null}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ position: 'relative' }}>
              <TablePagination
                rowsPerPageOptions={defaultRowsPerPageOptions}
                component="div"
                count={totalCount}
                rowsPerPage={tableData.rowsPerPage}
                page={tableData.page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleRowsPerPageChange}
                ActionsComponent={TablePaginationActions}
              />

              <FormControlLabel
                label="Dense"
                sx={{ px: 3, py: 1.5, top: 0, position: { md: 'absolute' } }}
                control={
                  <Switch
                    checked={tableData.dense}
                    onChange={tableData.onChangeDense}
                  />
                }
              />
            </Box>
          </CardContent>
        </Card>
      </div>
    </Fade>
  );
};

export default SitemapTab;
